import { memo, useCallback } from 'react';
import { ChatParticipant } from '../../types/chat.types';
import { mergeListWithInputParticipants } from '../../utils/chatParticipants.utils';
import useChatParticipants from '../ChatConfigViews/components/ChatContainerContent/hooks/useChatParticipants/useChatParticipants';
import ChatParticipantListContainer from './ChatParticipantListContainer';

type ChatParticipantListFieldProps = {
  value: ChatParticipant[];
  onAddParticipant: (participant: ChatParticipant) => void;
  onRemoveParticipant: (participant: ChatParticipant) => void;
};

const ChatParticipantListField = ({
  value,
  onAddParticipant,
  onRemoveParticipant,
}: ChatParticipantListFieldProps) => {
  const { removeChatParticipant, reinsertChatParticipant, loadMoreRows, changeQuery, state } =
    useChatParticipants();

  const addParticipant = useCallback(
    (participant: ChatParticipant) => {
      onAddParticipant(participant);
      removeChatParticipant(participant);
    },
    [onAddParticipant, removeChatParticipant],
  );

  const removeParticipant = useCallback(
    (participant: ChatParticipant) => {
      onRemoveParticipant(participant);
      reinsertChatParticipant(participant);
    },
    [onRemoveParticipant, reinsertChatParticipant],
  );

  const handleClick = useCallback(
    (participant: ChatParticipant) => {
      const found = value && value.find(({ id }: ChatParticipant) => id === participant.id);

      if (found) {
        removeParticipant(participant);
      } else {
        addParticipant(participant);
      }
    },
    [addParticipant, value, removeParticipant],
  );

  const participants = mergeListWithInputParticipants(value, state.participants);
  return (
    <ChatParticipantListContainer
      selectedParticipantsLength={value.length}
      participants={participants}
      multiselect
      isLoading={state.isLoading}
      loadMoreRows={loadMoreRows}
      changeQuery={changeQuery}
      onClick={handleClick}
    />
  );
};

export default memo(ChatParticipantListField);
